// Small shared component kit — the web equivalent of ../roja-app/src/components/*
// (Button, Card, Badge, …), same visual language via styles.css's tokens.
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

// ---- Button ----------------------------------------------------------------

type ButtonVariant = "primary" | "brand" | "secondary" | "ghost" | "danger";
type ButtonSize = "sm" | "md" | "lg";

export function Button({
  children,
  onClick,
  variant = "primary",
  size = "md",
  fullWidth,
  disabled,
  loading,
  type = "button",
  title,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  type?: "button" | "submit";
  title?: string;
}) {
  return (
    <button
      type={type}
      title={title}
      className={`btn ${variant} ${size !== "md" ? size : ""} ${fullWidth ? "full" : ""}`}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {loading ? <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> : children}
    </button>
  );
}

// ---- Card --------------------------------------------------------------------

export function Card({
  children,
  padding = "md",
  style,
}: {
  children: React.ReactNode;
  padding?: "none" | "md" | "lg";
  style?: React.CSSProperties;
}) {
  return (
    <div className={`card ${padding === "lg" ? "pad-lg" : padding === "none" ? "pad-none" : ""}`} style={style}>
      {children}
    </div>
  );
}

// ---- Pill / status badge ------------------------------------------------------

export type PillTone = "neutral" | "success" | "warning" | "danger" | "dark" | "brand";

export function Pill({ children, tone = "neutral" }: { children: React.ReactNode; tone?: PillTone }) {
  return <span className={`pill ${tone}`}>{children}</span>;
}

// ---- StatCard ------------------------------------------------------------------

export function StatCard({
  label,
  value,
  delta,
}: {
  label: string;
  value: string | number;
  delta?: { value: string; direction: "up" | "down" };
}) {
  return (
    <div className="stat-card">
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
      {delta ? <span className={`stat-delta ${delta.direction}`}>{delta.direction === "up" ? "▲" : "▼"} {delta.value}</span> : null}
    </div>
  );
}

// ---- Table ------------------------------------------------------------------

export type Column<T> = {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  width?: string;
};

export function Table<T>({
  columns,
  rows,
  keyField,
  onRowClick,
}: {
  columns: Column<T>[];
  rows: T[];
  keyField: (row: T) => string;
  onRowClick?: (row: T) => void;
}) {
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={c.width ? { width: c.width } : undefined}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={keyField(row)} className={onRowClick ? "clickable" : ""} onClick={() => onRowClick?.(row)}>
              {columns.map((c) => (
                <td key={c.key}>{c.render(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- EmptyState / Spinner ------------------------------------------------------

export function EmptyState({ title, body }: { title: string; body?: string }) {
  return (
    <div className="empty-state">
      <p className="t-title">{title}</p>
      {body ? <p className="t-small" style={{ marginTop: 6 }}>{body}</p> : null}
    </div>
  );
}

export function Spinner() {
  return (
    <div className="spinner-row">
      <span className="spinner" />
    </div>
  );
}

// ---- Modal --------------------------------------------------------------------

export function Modal({
  open,
  onClose,
  children,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="modal-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal-card ${wide ? "wide" : ""}`}>{children}</div>
    </div>
  );
}

// ---- PageHeader ------------------------------------------------------------------

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: React.ReactNode }) {
  return (
    <div className="page-header">
      <div>
        <h1 className="t-h1">{title}</h1>
        {subtitle ? <p className="t-small text-secondary" style={{ marginTop: 4 }}>{subtitle}</p> : null}
      </div>
      {actions ? <div className="row wrap">{actions}</div> : null}
    </div>
  );
}

// ---- Segmented control -----------------------------------------------------------

export function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="segmented">
      {options.map((o) => (
        <button key={o.value} className={o.value === value ? "active" : ""} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---- Avatar -----------------------------------------------------------------------

export function Avatar({ label }: { label: string }) {
  return <span className="avatar">{label}</span>;
}

// ---- MessageTicks -------------------------------------------------------------------

/** WhatsApp-style delivery ticks for a message *this side* sent — ✓ sent, ✓✓ delivered, ✓✓ (colored) read. */
export function MessageTicks({ status }: { status: "sent" | "delivered" | "read" }) {
  return (
    <span
      title={status.charAt(0).toUpperCase() + status.slice(1)}
      style={{ color: status === "read" ? "var(--status-success)" : "currentColor", opacity: status === "read" ? 1 : 0.7 }}
    >
      {status === "sent" ? "✓" : "✓✓"}
    </span>
  );
}

// ---- OnlineDot ----------------------------------------------------------------------

/** A small presence dot + label — pass the {online, label} shape format.ts's presenceFromLastActiveAt() returns. */
export function OnlineDot({ online, label }: { online: boolean; label: string }) {
  return (
    <span className="row" style={{ gap: 5, alignItems: "center" }} title={label}>
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: online ? "var(--status-success)" : "var(--text-tertiary)",
          flexShrink: 0,
        }}
      />
      <span className="t-small text-secondary">{label}</span>
    </span>
  );
}

// ---- Toasts -----------------------------------------------------------------------

type Toast = { id: number; message: string; tone: "info" | "error" };
type ToastContextValue = { show: (message: string, tone?: "info" | "error") => void };

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = useCallback((message: string, tone: "info" | "error" = "info") => {
    if (!message) return; // errorMessage() returns "" for a benign auto-cancelled request — nothing to show
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, tone }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div className="toast-stack">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone}`}>
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

/**
 * Extracts a readable message from a PocketBase ClientResponseError-ish
 * rejection (custom pb_hooks routes included — see pocketbase.ts). Returns
 * "" for a request the SDK itself auto-cancelled (isAbort) — a *newer*
 * identical/overlapping request superseded it (see e.g. Dashboard.tsx's
 * countOf/fetchDailyRollups comments), so its own result is about to render
 * instead; there's nothing wrong to show the user. `show()` in this file
 * treats an empty message as "don't toast".
 */
export function errorMessage(err: unknown): string {
  if (err && typeof err === "object" && "isAbort" in err && (err as { isAbort?: boolean }).isAbort) return "";
  if (err && typeof err === "object" && "message" in err) return String((err as { message: unknown }).message);
  return "Something went wrong.";
}
